import {
  ReactNode,
  Ref,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import AddPortfolioModal from "@features/add/AddPortfolioModal";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import { ImportType, ImportTypeRef } from "@features/add/components/types";
import { useMandatorVisibility } from "@helpers";
import { ResponsiveDropdown } from "@components";
import { accountTypeIconMapper } from "@icons/utils";

interface AddOption extends ImportType {
  text: string;
  testId: string;
  default: AccountTypeEnum;
}

export const addOptions: AddOption[] = [
  {
    text: "depotPage.addOptions.depots",
    testId: "depots",
    type: "",
    default: "01_depot",
  },
  {
    text: "depotPage.addOptions.cash",
    testId: "cash",
    type: "",
    default: "02_cash",
  },
  {
    text: "depotPage.addOptions.assets",
    testId: "assets",
    type: "manually",
    default: "05_material_assets",
  },
  {
    text: "depotPage.addOptions.insurance",
    testId: "insurance",
    type: "manually",
    default: "04_insurance",
  },
];

export interface AddPortfolioHandle {
  // open the modal seeded to a specific import type
  onSelect: (val: ImportType) => void;
  // open the modal at the selection grid
  openModal: () => void;
}

interface Props {
  ref?: Ref<AddPortfolioHandle>;
  // the button that opens the dropdown / modal
  anchor: ReactNode;
  // import type used when a mandator opens the modal directly (e.g. depots → 01_depot)
  directOpenType?: ImportType;
  mobileTitle?: string;
  id?: string;
}

export const AddPortfolio = ({
  ref,
  anchor,
  directOpenType,
  mobileTitle,
  id = "new-portfolio-menu",
}: Props) => {
  const { t } = useTranslation();
  const modalRef = useRef<ImportTypeRef | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModalDirectly = useMandatorVisibility({
    showFor: ["kpmg-germany"],
  });

  const onSelect = useCallback((val: ImportType) => {
    setIsModalOpen(true);
    modalRef.current?.setImportType(val);
  }, []);

  const openModal = useCallback(() => onSelect({ type: "" }), [onSelect]);

  useImperativeHandle(ref, () => ({ onSelect, openModal }), [
    onSelect,
    openModal,
  ]);

  const items = addOptions.map((option) => ({
    label: t(option.text),
    testId: `${option.testId}-create`,
    icon: accountTypeIconMapper[option.default],
    onClick: () => onSelect(option),
  }));

  return (
    <>
      {openModalDirectly ? (
        <Box
          display="inline-flex"
          onClick={() => onSelect(directOpenType ?? { type: "" })}
        >
          {anchor}
        </Box>
      ) : (
        <ResponsiveDropdown
          id={id}
          mobileTitle={mobileTitle}
          items={items}
          anchor={anchor}
        />
      )}
      <AddPortfolioModal
        ref={modalRef}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
    </>
  );
};
